import React from 'react';
import { Button, Box, Typography, DialogTitle, DialogContent } from '@mui/material';
import { t } from 'i18next';
import { toast } from 'react-toastify';

import RenderIf from '../../../renderIf';
import useCurrencyDisplay from '../../../../utils/useCurrencyDisplay';
import Markdown from '../markdown';
import { useStyles } from '../styles';

const tableStyles = {
    '&.table.first.MuiBox-root': {
        width: '100%',
        margin: 'auto 0 calc(30rem/16) auto',
    },
    '& tbody tr': {
        display: 'flex',
        width: 'calc(450rem/16)',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: 0,
        padding: 'calc(8rem/16) 0',
        fontSize: 'calc(14rem/16)',
        color: (theme) => theme.palette.grey.header,
        fontWeight: 600,
        '&:not(:last-child)': {
            borderBottom: '1px solid rgb(151, 151, 151, 0.24)',
        },
        '& td:first-child': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            '& .descriptive': {
                fontStyle: 'italic',
            },
        },
        '& td:last-child': {
            color: (theme) => theme.palette.grey['field-text'],
        },
        '& .MuiBox-root.dollar-amount': {
            display: 'flex',
            flexDirection: 'row',
            '&, & > div': {
                width: 'auto',
                padding: 0,
            },
            '& .currency': {
                mr: 'calc(2rem/16)',
            },
        },
    },
};

const FormattedCurrency = ({ val }) => {
    const { translateCurrency } = useCurrencyDisplay();
    const amount = translateCurrency(val).replace('$', '');
    return (
        <Box className="dollar-amount">
            <Box className="currency">$</Box>
            <Box className="amount">{amount}</Box>
        </Box>
    );
};

export const ScholarshipTables = ({ scholarshipData }) => {
    return (
        <>
            <Box
                component="table"
                className="table first"
                sx={{ ...tableStyles }}
            >
                <colgroup>
                    <col />
                    <col />
                    <col />
                    <col />
                </colgroup>

                <tbody>
                    <tr>
                        <td>{t('AcceptDecline.Calc1')}</td>
                        <td>
                            <FormattedCurrency val={scholarshipData.InitialStateAidBaseAmt}></FormattedCurrency>
                        </td>
                    </tr>

                    <tr>
                        <td>{t('AcceptDecline.Calc2')}</td>
                        <td>{`${scholarshipData.GradeWeight} (${scholarshipData.GradeLevel})`}</td>
                    </tr>

                    <tr>
                        <td>{t('AcceptDecline.Calc3')}</td>
                        <td>
                            <RenderIf isTrue={scholarshipData.PrimDisabWeight !== 0}>
                                {`${scholarshipData.PrimDisabWeight} (${!!scholarshipData?.PrimDisabCode ? scholarshipData.PrimDisabCode : 'N/A'})`}
                            </RenderIf>
                            <RenderIf isTrue={scholarshipData.PrimDisabWeight === 0}>--</RenderIf>
                        </td>
                    </tr>

                    <tr>
                        <td>{t('AcceptDecline.Calc4')}</td>
                        <td>
                            <RenderIf isTrue={scholarshipData.SecDisabWeight !== 0}>
                                {`${scholarshipData.SecDisabWeight} (${!!scholarshipData?.SecDisabCode ? scholarshipData.SecDisabCode : 'N/A'})`}
                            </RenderIf>
                            <RenderIf isTrue={scholarshipData.SecDisabWeight === 0}>--</RenderIf>
                        </td>
                    </tr>

                    <tr>
                        <td colSpan={3}>{t('AcceptDecline.WeightTotals')}</td>
                        <td>{scholarshipData.TotalWeight}</td>
                    </tr>
                </tbody>
            </Box>

            <Box
                component="table"
                sx={{
                    ...tableStyles,
                }}
            >
                <tbody>
                    <tr>
                        <td>
                            <div>{t('AcceptDecline.Scholarship1')}</div>
                            <div className="descriptive">(Total Weight times State Aid Factor)</div>
                        </td>
                        <td>
                            <FormattedCurrency val={scholarshipData.MaxScholarshipAmtCalc}></FormattedCurrency>
                        </td>
                    </tr>

                    <tr>
                        <td>{t('AcceptDecline.Scholarship2')}</td>
                        <td>
                            <FormattedCurrency val={scholarshipData.AdminFee}></FormattedCurrency>
                        </td>
                    </tr>

                    <tr>
                        <td>{t('AcceptDecline.Scholarship3')}</td>
                        <td>
                            <FormattedCurrency val={scholarshipData.NetScholarshipAmtCalc}></FormattedCurrency>
                        </td>
                    </tr>

                    <tr>
                        <td>{t('AcceptDecline.Scholarship4')}</td>
                        <td>
                            <FormattedCurrency val={scholarshipData.QrtlyScholarshipAmtCalc}></FormattedCurrency>
                        </td>
                    </tr>
                </tbody>
            </Box>
        </>
    );
};

const Choice = (props) => {
    const { schoolName, status, scholarshipData, handleStepChange, handleClose } = props;

    const styles = useStyles();

    const [opt, setOpt] = React.useState('');
    const [invalid, setInvalid] = React.useState(false);

    const next = () => {
        if (opt !== '') {
            setInvalid(false);
            handleStepChange(opt === 'true');
        } else {
            setInvalid(true);
            toast.error(t('General.RequiredFields'));
        }
    };

    return (
        <>
            <DialogTitle>{status !== 9 ? t('AcceptDecline.Header1') : t('AcceptDecline.Header4')}</DialogTitle>
            <DialogContent
                sx={{
                    '& tr td div': {
                        padding: 'calc(8rem/16)',
                        width: '7rem',
                        textAlign: 'center',
                        margin: '0 calc(16rem/16)',
                    },
                    '& tr td:first-child div': {
                        width: 'auto',
                        textAlign: 'right',
                        margin: 0,
                        padding: 0,
                    },
                    '& td:first-of-type': {
                        textAlign: 'right',
                        fontWeight: 600,
                    },
                }}
            >
                <Box
                    sx={{
                        '& p.MuiTypography-root': {
                            color: (theme) => theme.palette.grey.header,
                        },
                    }}
                >
                    <Box
                        sx={{
                            marginBottom: '1rem',
                            textAlign: 'left',
                            '& *': {
                                fontSize: 'calc(15rem/16)',
                                lineHeight: 1.2,
                            },
                            '& p': {
                                marginBottom: 'calc(4rem/16)',
                                paddingRight: 'calc(12rem/16)',
                            },
                        }}
                    >
                        <RenderIf isTrue={status !== 9}>
                            <Markdown>
                                {t('AcceptDecline.Step1Explanation1', {
                                    schoolName: schoolName.trim(),
                                })}
                            </Markdown>
                            <Markdown>{t('AcceptDecline.Step1Explanation2')}</Markdown>
                            <Markdown>{t('AcceptDecline.ScholarshipCalculation')}</Markdown>
                        </RenderIf>

                        <RenderIf isTrue={status === 9}>
                            <Markdown>{t('AcceptDecline.AcceptedCalculation')}</Markdown>
                        </RenderIf>
                    </Box>

                    <ScholarshipTables scholarshipData={scholarshipData}></ScholarshipTables>

                    <RenderIf isTrue={status !== 9}>
                        <Box
                            sx={{
                                '&.MuiBox-root p': {
                                    margin: 'calc(30rem/16) 0 calc(20rem/16) 0',
                                    fontWeight: 400,
                                    paddingRight: 'calc(12rem/16)',
                                    '& *': {
                                        fontSize: 'calc(15rem/16)',
                                        color: (theme) => theme.palette.grey.header,
                                    },
                                    '& span': {
                                        fontWeight: 600,
                                    },
                                    '& span:last-child': {
                                        color: (theme) => theme.palette.primary.main,
                                    },
                                },
                            }}
                        >
                            <Markdown>{t('AcceptDecline.ResponseInstructions')}</Markdown>
                        </Box>
                    </RenderIf>

                    <RenderIf isTrue={status !== 9}>
                        <Box
                            sx={{
                                '&.MuiBox-root p': {
                                    margin: '0 0 calc(5rem/16) 0',
                                    fontWeight: 600,
                                    fontSize: 'calc(13rem/16)',
                                    color: (theme) => theme.palette.grey.header,
                                },
                                '& select': {
                                    fontSize: 'calc(14rem/16)',
                                    border: (theme) => `1px solid ${theme.palette.grey.border}`,
                                },
                            }}
                        >
                            <Typography color={invalid ? 'error' : null}>
                                <span className={styles.required}>*</span> {t('AcceptDecline.SelectLabel')}
                            </Typography>
                            <select
                                id="accept-decline-select"
                                value={opt}
                                onChange={(event) => {
                                    setOpt(event.target.value);
                                    setInvalid(false);
                                }}
                                className={invalid ? [styles.invalid, styles.select].join(' ') : styles.select}
                            >
                                <option value="" />
                                <option value={true}>{t('AcceptDecline.AcceptScholarship')}</option>
                                <option value={false}>{t('AcceptDecline.DeclineScholarship')}</option>
                            </select>
                        </Box>
                    </RenderIf>

                    <RenderIf isTrue={status === 9}>
                        <div className="acceptdecline-note">
                            <Markdown>{t('AcceptDecline.AcceptedNote')}</Markdown>
                        </div>
                    </RenderIf>
                </Box>
                <Box>
                    <Button
                        onClick={() => handleClose?.()}
                        variant="contained"
                        sx={{ marginRight: '2.25rem' }}
                        color="secondary"
                    >
                        <Typography>{t('General.Cancel')}</Typography>
                    </Button>
                    <RenderIf isTrue={status !== 9}>
                        <Button
                            onClick={next}
                            variant="contained"
                        >
                            <Typography>Next</Typography>
                        </Button>
                    </RenderIf>
                </Box>
            </DialogContent>
        </>
    );
};

export default Choice;
